import React, { useState, useEffect } from "react";
import { Box, Typography, Link, useMediaQuery, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import AuthenticationLeftContent from "../../../components/common/AutenticationLeftContent";
import AuthenticationTextInput from "../../../components/common/TextInput/AuthenticationTextInput";
import PrimaryButton from "../../../components/common/PrimaryButton";
import Heading from "../../../components/common/AutenticationTitles/Heading";
import Subtitle from "../../../components/common/AutenticationTitles/Subtitle";
import { useDispatch, useSelector } from "react-redux";
import { forgotPasswordAction, setResetEmail } from "../../../store/slices/authSlice";

const ForgotPasswordPage = ({ onClose, onViewChange, isMobile = false }) => {
  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");
  
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const loading = false; // fallback if selector not used
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.auth.loading);
  const authError = useSelector((state) => state.auth.error);
  
  // Update error message when auth error changes
  useEffect(() => {
    if (authError) {
      // Check if error message contains "user not found" or similar
      const errorMessage = authError.toLowerCase();
      if (errorMessage.includes("user not found") || 
          errorMessage.includes("no user found") || 
          errorMessage.includes("user does not exist") ||
          errorMessage.includes("email not found")) {
        setError("No user found with this email");
      } else {
        setError(authError);
      }
    } else {
      setError("");
    }
  }, [authError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors
    if (!email) {
      setError("Email is required");
      return;
    }
    try {
      const result = await dispatch(forgotPasswordAction(email));
      if (forgotPasswordAction.fulfilled.match(result)) {
        dispatch(setResetEmail(email));
        // If onViewChange is available (ResponsiveAuthWrapper), use it
        // Otherwise, navigate to verification page
        if (onViewChange) {
          onViewChange("verify");
        } else {
          navigate("/verification", { state: { email } });
        }
      } else if (forgotPasswordAction.rejected.match(result)) {
        // Error will be handled by useEffect above
        const errorMessage = result.payload || result.error?.message || "Failed to send reset code";
        const errorLower = errorMessage.toLowerCase();
        if (errorLower.includes("user not found") || 
            errorLower.includes("no user found") || 
            errorLower.includes("user does not exist") ||
            errorLower.includes("email not found")) {
          setError("No user found with this email");
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      const errorMessage = err?.message || "Failed to send reset code";
      const errorLower = errorMessage.toLowerCase();
      if (errorLower.includes("user not found") || 
          errorLower.includes("no user found") || 
          errorLower.includes("user does not exist") ||
          errorLower.includes("email not found")) {
        setError("No user found with this email");
      } else {
        setError(errorMessage);
      }
    }
  };

  return (
    <Box style={{ 
      display: "flex", 
      flexDirection: isMobile ? "column" : isTablet ? "column" : "row",
      backgroundColor: "#FFFFFF", 
      borderRadius: isMobile ? "12px" : isTablet ? "12px" : "22px",
      position: "relative", 
      padding: isMobile ? "0px" : isTablet ? "0px" : "20px 0px 20px 20px",
      height: isMobile ? "100vh" : isTablet ? "100vh" : "auto",
      overflow: isMobile ? "hidden" : isTablet ? "hidden" : "visible"
    }}>
      <Box className="left-width" flex={1} sx={{maxWidth:isMobile ? "100%": isTablet ? "100%" : isLaptop? "300px" :"499px"}}>
        <AuthenticationLeftContent onClose={onClose} />
      </Box>
      <Box className="right-content" flex={1} p={isMobile ? 2 : isTablet ? 2 : 4} sx={{
        maxWidth:isMobile?"100%":isLaptop?"390px": "100%",
        marginInline: isMobile ? "0" : isTablet ? "0" : "auto",
        height: isMobile ? "50vh" : isTablet ? "60vh" : "auto",
        overflow: isMobile ? "auto" : isTablet ? "auto" : "visible",
        display: "flex",
        flexDirection: "column",
        justifyContent: isMobile ? "flex-start" : "center",
        width: isMobile ? "100%" : isTablet ? "100%" : "auto",
        position: "relative",
        borderRadius: isMobile ? "0 0 12px 12px" : isTablet ? "0 0 12px 12px" : "0"
      }}>
        <Heading>Forgot your password?</Heading>
        <Subtitle>Please enter the details to reset your the password</Subtitle>
        <form onSubmit={handleSubmit}>
          <AuthenticationTextInput
            label="Email Address"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Enter Email Address"
            error={error}
            helperText={error}
            required
          />
          <PrimaryButton disabled={isLoading} sx={{ position: "relative" }}>
            {isLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              </Box>
            ) : (
              "Submit"
            )}
          </PrimaryButton>
        </form>
        <Typography align="center" mt={3} style={{ fontSize: isMobile ? "14px" : isTablet ? "14px" : "16px", fontWeight: "500", color: "#4A4A49" }}>
          <Link
            href="#"
            underline="none"
            onClick={e => {
              e.preventDefault();
              if (onViewChange) onViewChange("login");
            }}
            style={{ fontSize: isMobile ? "14px" : isTablet ? "14px" : "16px", fontWeight:"700", color: "#141414", cursor: "pointer" }}
          >
            Back to Login
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;